// src/components/GridView/GridContextMenu.tsx
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { GridColumn } from './types';
import { getColumnApiKey } from './hooks/useHiddenColumns';

// Fixed column keys that cannot be hidden
const FIXED_KEYS = new Set(['col-title', 'col-created-at']);

// ==================== Props ====================

interface GridContextMenuProps {
  x: number;
  y: number;
  /** Column the user right-clicked on (null if outside data area) */
  targetColumn: GridColumn | null;
  /** All dynamic columns including currently hidden ones */
  allDynamicColumns: GridColumn[];
  hiddenKeys: Set<string>;
  onHide: () => void;
  onShow: (key: string) => void;
  onManageColumns: () => void;
  onClose: () => void;
}

// ==================== Component ====================

export function GridContextMenu({
  x,
  y,
  targetColumn,
  allDynamicColumns,
  hiddenKeys,
  onHide,
  onShow,
  onManageColumns,
  onClose,
}: GridContextMenuProps)
{
  const menuRef = useRef<HTMLDivElement>(null);
  const [showSubmenu, setShowSubmenu] = useState(false);

  // Adjust menu position after layout to stay within viewport (no visual flash)
  useLayoutEffect(() =>
  {
    const menu = menuRef.current;
    if (!menu) return;
    const { width, height } = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (x + width > vw) menu.style.left = `${Math.max(0, x - width)}px`;
    if (y + height > vh) menu.style.top = `${Math.max(0, y - height)}px`;
  }, [x, y]);

  // Close on Escape
  useEffect(() =>
  {
    const handler = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Derived state
  const isDark = document.documentElement.classList.contains('dark');
  const canHide = targetColumn != null && !FIXED_KEYS.has(targetColumn.key);
  const hiddenColumnsList = allDynamicColumns.filter(col => hiddenKeys.has(getColumnApiKey(col)));
  const hasHidden = hiddenColumnsList.length > 0;

  const menu = (
    <>
      {/* Transparent backdrop — closes menu on outside click */}
      <div className="gridview-context-backdrop" onClick={onClose} />

      {/* Menu panel */}
      <div ref={menuRef} className="gridview-context-menu" style={{ left: x, top: y }}>
        {/* Hide Column */}
        <div
          className={`gridview-context-menu-item${canHide ? '' : ' disabled'}`}
          onClick={canHide ? onHide : undefined}
        >
          <span>Hide Column</span>
        </div>

        {/* Divider */}
        <div className="gridview-context-menu-divider" />

        {/* Show Column — with inline submenu */}
        <div
          className={`gridview-context-menu-item${hasHidden ? '' : ' disabled'}`}
          onMouseEnter={() => hasHidden && setShowSubmenu(true)}
          onMouseLeave={() => setShowSubmenu(false)}
        >
          <span>Show Column</span>
          <span className="gridview-context-menu-arrow">▶</span>
          {showSubmenu && hasHidden && (
            <div className="gridview-context-submenu">
              {hiddenColumnsList.map(col =>
              {
                const key = getColumnApiKey(col);
                return (
                  <div
                    key={key}
                    className="gridview-context-menu-item"
                    style={{ paddingLeft: 12, display: 'block' }}
                    onClick={e =>
                    {
                      e.stopPropagation();
                      onShow(key);
                    }}
                  >
                    <div
                      style={{
                        width: 3,
                        height: 14,
                        borderRadius: 2,
                        backgroundColor: col.layerColor ?? (isDark ? '#4a4c4c' : '#d1d5db'),
                        flexShrink: 0,
                        position: 'absolute',
                        top: '8px',
                        left: '2px',
                      }}
                    />
                    <span>{col.title}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Manage Columns */}
        <div className="gridview-context-menu-item" onClick={onManageColumns}>
          <span>Manage Columns</span>
        </div>
      </div>
    </>
  );

  return createPortal(menu, document.body);
}
